// src/pages/ViewTechnicalReportPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useCompany } from '../context/CompanyContext';
import { technicalReportService } from '../services/technicalReportService';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { PrinterIcon, EnvelopeIcon, PencilSquareIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';

const TYPE_NAMES = {
    generico: 'Informe Genérico',
    imagenes: 'Informe de Imágenes',
    verificacion_inicial: 'Verificación Inicial (TE1)',
    memoria_explicativa: 'Memoria Explicativa',
    pericial: 'Informe Pericial',
    calidad_energia: 'Fallas de Calidad Energética'
};

const ReadOnlyField = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-3 print:col-span-3' : ''}>
        <p className="block text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 print:text-gray-500 mb-1">{label}</p>
        <div className="text-sm text-gray-900 dark:text-gray-100 print:text-gray-900 bg-gray-50 dark:bg-gray-800 print:bg-gray-50 p-2 rounded border border-gray-100 dark:border-gray-700 print:border-gray-200 min-h-[2.5rem] whitespace-pre-wrap flex items-center">{value || '-'}</div>
    </div>
);

// --- Modal de Email ---
const EmailModal = ({ report, reportId, onClose, setNotification, companyName }) => {
    const { user } = useAuth();
    const [recipient, setRecipient] = useState(report.clientEmail || '');
    const [cc, setCc] = useState('');
    const [sending, setSending] = useState(false);

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const generatePDF = httpsCallable(functions, 'generateTechnicalReportPDF');
            await generatePDF({
                reportId,
                emailTo: recipient,
                emailCc: cc,
                emailSubject: `${TYPE_NAMES[report.type] || 'Informe Técnico'} - ${report.titulo || reportId} - ${companyName}`,
                emailBody: `
                    <div style="font-family: Arial, sans-serif; padding: 20px;">
                        <h2>${TYPE_NAMES[report.type] || 'Informe Técnico'}</h2>
                        <p>Estimado/a <b>${report.clientName || 'Cliente'}</b>,</p>
                        <p>Adjuntamos a este correo el informe técnico <b>"${report.titulo}"</b>.</p>
                        <p>El documento incluye el detalle completo del análisis realizado en formato PDF profesional.</p>
                        <br><p>Saludos cordiales,<br><b>${companyName}</b></p>
                    </div>
                `
            });
            setNotification({ message: 'Informe enviado con éxito.', type: 'success' });
            onClose();
        } catch (error) {
            console.error(error);
            setNotification({ message: `Error: ${error.message}`, type: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 w-full max-w-lg shadow-2xl border border-transparent dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6 text-blue-600 dark:text-blue-400">
                    <EnvelopeIcon className="w-6 h-6" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Enviar Informe</h2>
                </div>
                <form onSubmit={handleSendEmail}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium dark:text-gray-300">Para:</label>
                        <input type="email" value={recipient} onChange={e => setRecipient(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium dark:text-gray-300">CC (Copia a):</label>
                        <input type="email" value={cc} onChange={e => setCc(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="correo@ejemplo.com" />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button type="button" onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg">Cancelar</button>
                        <button type="submit" disabled={sending} className="bg-blue-600 text-white px-4 py-2 rounded-lg disabled:bg-blue-300">
                            {sending ? 'Enviando...' : 'Enviar PDF'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Página Principal ---
const ViewTechnicalReportPage = ({ reportId, setPage, setNotification }) => {
    const { user } = useAuth();
    const { isAdmin, canView, canEdit, canDelete } = usePermissions();
    const { name: companyName } = useCompany();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEmailModal, setShowEmailModal] = useState(false);

    useEffect(() => {
        if (!isAdmin && !canView('techReports')) {
            setNotification({ message: 'No tienes permiso para ver este informe.', type: 'error' });
            setPage('techReports');
            return;
        }

        const fetchReport = async () => {
            try {
                const data = await technicalReportService.getById(reportId);
                setReport(data);
            } catch (err) {
                setNotification({ message: 'Error al cargar informe.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        if (reportId) fetchReport();
    }, [reportId, setNotification, isAdmin, canView, setPage]);

    const formatDate = (ts) => {
        if (!ts) return '-';
        if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString('es-CL');
        return ts;
    };

    if (loading) return <div className="p-8 text-center text-gray-600 dark:text-gray-400">Cargando informe...</div>;
    if (!report) return <div className="p-8 text-center text-red-600">Informe no encontrado.</div>;

    return (
        <div className="p-4 md:p-8 print:p-4 bg-gray-50 dark:bg-gray-900 print:bg-white min-h-screen print:min-h-0">
            {/* Barra de acciones */}
            <div className="max-w-5xl mx-auto flex flex-wrap justify-between items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 print:text-gray-900">{report.titulo || 'Informe Técnico'}</h1>
                    <div className="flex items-center gap-3 mt-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-600 font-semibold">{TYPE_NAMES[report.type] || report.type}</p>
                        {report.folio && <span className="bg-gray-200 dark:bg-gray-700 print:bg-gray-200 text-gray-700 dark:text-gray-300 print:text-gray-700 text-xs px-2 py-0.5 rounded-full font-mono">Folio: {report.folio}</span>}
                    </div>
                </div>
                <div className="flex gap-2 print:hidden">
                    <button onClick={() => setPage('techReports')} className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex items-center gap-1"><ArrowLeftIcon className="w-4 h-4" /> Volver</button>
                    {canEdit('techReports') && (
                        <button onClick={() => setPage('techReportForm', null, reportId)} className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 border dark:border-yellow-800 hover:shadow transition">
                            <PencilSquareIcon className="w-5 h-5" /> Editar
                        </button>
                    )}
                    <button onClick={() => window.print()} className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 border dark:border-gray-600 hover:shadow transition">
                        <PrinterIcon className="w-5 h-5" /> Imprimir
                    </button>
                    <button onClick={() => setShowEmailModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow hover:bg-blue-700 transition">
                        <EnvelopeIcon className="w-5 h-5" /> Enviar PDF
                    </button>
                </div>
            </div>

            {/* Contenido del Informe */}
            <div className="max-w-5xl mx-auto space-y-6">
                {/* Datos del Cliente */}
                <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                    <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Datos del Cliente</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4">
                        <ReadOnlyField label="Cliente" value={report.clientName} />
                        <ReadOnlyField label="RUT" value={report.clientId} />
                        <ReadOnlyField label="Fecha" value={formatDate(report.fecha)} />
                        <ReadOnlyField label="Ubicación" value={report.ubicacion} fullWidth />
                    </div>
                </div>

                {/* Según el tipo de informe */}
                {report.type === 'generico' && (
                    <>
                        {report.antecedentes && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Antecedentes</h2>
                                <p className="text-sm text-gray-700 dark:text-gray-300 print:text-gray-700 whitespace-pre-wrap">{report.antecedentes}</p>
                            </div>
                        )}
                        {report.sections && report.sections.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Contenido del Informe</h2>
                                <div className="space-y-4">
                                    {report.sections.map((s, i) => (
                                        <div key={i} className="border-l-4 border-blue-500 pl-4 break-inside-avoid">
                                            <h3 className="font-bold text-gray-800 dark:text-gray-100 print:text-gray-800">{s.titulo || `Sección ${i + 1}`}</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-600 whitespace-pre-wrap mt-1">{s.contenido}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(report.conclusiones || report.recomendaciones) && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Conclusiones y Recomendaciones</h2>
                                {report.conclusiones && <ReadOnlyField label="Conclusiones" value={report.conclusiones} fullWidth />}
                                {report.recomendaciones && <ReadOnlyField label="Recomendaciones" value={report.recomendaciones} fullWidth />}
                            </div>
                        )}
                    </>
                )}

                {report.type === 'imagenes' && (
                    <>
                        {report.descripcionGeneral && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Descripción General</h2>
                                <p className="text-sm text-gray-700 dark:text-gray-300 print:text-gray-700 whitespace-pre-wrap">{report.descripcionGeneral}</p>
                            </div>
                        )}
                        {report.observaciones && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Observaciones Finales</h2>
                                <p className="text-sm text-gray-700 dark:text-gray-300 print:text-gray-700 whitespace-pre-wrap">{report.observaciones}</p>
                            </div>
                        )}
                        {(report.conclusiones || report.recomendaciones) && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Conclusiones y Recomendaciones</h2>
                                {report.conclusiones && <ReadOnlyField label="Conclusiones" value={report.conclusiones} fullWidth />}
                                {report.recomendaciones && <ReadOnlyField label="Recomendaciones" value={report.recomendaciones} fullWidth />}
                            </div>
                        )}
                    </>
                )}

                {/* VISUALIZACIÓN INFORME TE1 */}
                {report.type === 'verificacion_inicial' && (
                    <>
                        {/* Datos de la Obra */}
                        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 break-inside-avoid">
                            <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">1. Detalles de la Obra</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 print:grid-cols-4 gap-4">
                                <ReadOnlyField label="Mandante" value={report.te1Mandante} />
                                <ReadOnlyField label="RUT Mandante" value={report.te1RutMandante} />
                                <ReadOnlyField label="Rep. Legal" value={report.te1RepLegal} />
                                <ReadOnlyField label="RUT Rep. Legal" value={report.te1RutRepLegal} />
                                <ReadOnlyField label="Estado Tiempo" value={report.te1Clima} />
                            </div>
                        </div>

                        {/* Instrumentos */}
                        {report.instrumentos && report.instrumentos.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">2. Instrumentos (Anexos Certificados en PDF)</h2>
                                <div className="grid gap-3">
                                    {report.instrumentos.map((inst, i) => (
                                        <div key={i} className="flex flex-wrap items-center justify-between p-3 bg-gray-50 dark:bg-gray-750 print:bg-gray-50 border dark:border-gray-700 print:border-gray-200 rounded">
                                            <div>
                                                <span className="font-bold text-gray-800 dark:text-gray-200 print:text-gray-800 mr-2">{inst.marca || 'S/M'} {inst.modelo || ''}</span>
                                                <span className="text-gray-500 text-sm">S/N: {inst.serie || 'N/A'}</span>
                                            </div>
                                            {inst.certUrl && <a href={inst.certUrl} target="_blank" rel="noreferrer" className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">Ver Certificado</a>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* RIC 19 */}
                        {report.ric19Checklist && report.ric19Checklist.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">3. Comprobación Condiciones Básicas (RIC 19)</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-100 dark:bg-gray-700 print:bg-gray-100 text-gray-600 dark:text-gray-300 print:text-gray-600">
                                            <tr>
                                                <th className="p-2">Item</th><th className="p-2">Aspecto</th><th className="p-2">Estado</th><th className="p-2">Obs.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.ric19Checklist.map((item) => (
                                                <tr key={item.id} className="border-b dark:border-gray-700 print:border-gray-200">
                                                    <td className="p-2 font-bold dark:text-gray-300 print:text-gray-800">{item.id}</td>
                                                    <td className="p-2 text-xs text-gray-600 dark:text-gray-400 print:text-gray-600 max-w-sm truncate" title={item.text}>{item.text.substring(0, 50)}...</td>
                                                    <td className="p-2 font-bold"><span className={item.status === 'SI' ? 'text-green-600' : item.status === 'NO' ? 'text-red-600' : 'text-gray-500'}>{item.status}</span></td>
                                                    <td className="p-2 text-xs dark:text-gray-400 print:text-gray-600">{item.obs || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Pruebas Generales */}
                        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                            <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">4. Pruebas y Ensayos Generales</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <ReadOnlyField label="R. Puesta a Tierra (Ω)" value={report.resistenciaTierra} />
                                <ReadOnlyField label="Continuidad Cond. (Ω)" value={report.continuidadConductores} />
                            </div>
                        </div>

                        {/* Tableros e Impedancia */}
                        {report.tableros && report.tableros.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">5. Impedancia Bucle Falla (Tableros)</h2>
                                <div className="space-y-4">
                                    {report.tableros.map((t, i) => (
                                        <div key={i} className="p-4 bg-gray-50 dark:bg-gray-750 print:bg-gray-50 rounded border dark:border-gray-700 print:border-gray-200">
                                            <h3 className="font-bold text-gray-800 dark:text-gray-200 print:text-gray-800 mb-2">{t.nombre}</h3>
                                            <div className="flex gap-4">
                                                <div className="text-xs"><span className="font-bold text-gray-500">L1/NPE:</span> ZS={t.l1zs||'-'}, IPCC={t.l1ipcc||'-'}</div>
                                                <div className="text-xs"><span className="font-bold text-gray-500">L2/NPE:</span> ZS={t.l2zs||'-'}, IPCC={t.l2ipcc||'-'}</div>
                                                <div className="text-xs"><span className="font-bold text-gray-500">L3/NPE:</span> ZS={t.l3zs||'-'}, IPCC={t.l3ipcc||'-'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Circuitos */}
                        {report.circuitos && report.circuitos.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">6. Medición de Circuitos (Anexos en PDF)</h2>
                                <div className="space-y-3">
                                    {report.circuitos.map((c, i) => (
                                        <div key={i} className="flex flex-wrap text-sm gap-2 border dark:border-gray-700 print:border-gray-200 p-3 bg-gray-50 dark:bg-gray-750 print:bg-gray-50">
                                            <div className="font-bold text-gray-800 dark:text-gray-200 print:text-gray-800 w-full mb-1">{c.nombre} {c.fotoUrl && <span className="text-xs font-normal text-green-600 ml-2 bg-green-50 px-2 py-0.5 rounded">📷 Medición adjunta</span>}</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">Voltaje</span> {c.vn} V</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">Imp. LN</span> {c.impLN} Ω</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">Imp. LT</span> {c.impLT} Ω</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">I.IPS</span> {c.ipsc} A</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">I.IPC</span> {c.ipfc} A</div>
                                            <div className="w-20"><span className="text-xs text-gray-500 block">Aislamiento</span> {c.resLTP} MΩ</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Conclusiones Finales */}
                        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                            <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">7. Parámetros Normativos y Conclusiones</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4">
                                {report.conclusionesEquipotencialidad && <ReadOnlyField label="Equipotencialidad" value={report.conclusionesEquipotencialidad} />}
                                {report.conclusionesAislacion && <ReadOnlyField label="Aislación" value={report.conclusionesAislacion} />}
                                {report.conclusionesDiferenciales && <ReadOnlyField label="Protección Diferencial" value={report.conclusionesDiferenciales} />}
                                {report.conclusionesPolaridad && <ReadOnlyField label="Polaridad e Iluminación" value={report.conclusionesPolaridad} />}
                                {report.conclusiones && <ReadOnlyField label="Conclusión General" value={report.conclusiones} fullWidth />}
                            </div>
                        </div>

                        {/* ANEXOS FOTOGRÁFICOS TE1 */}
                        {(report.instrumentos?.some(i => i.certUrl) || report.circuitos?.some(c => c.fotoUrl)) && (
                            <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                                <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Anexos: Registros y Certificados TE1</h2>
                                
                                {report.instrumentos?.some(i => i.certUrl) && (
                                    <div className="mb-6">
                                        <h3 className="font-bold text-gray-700 dark:text-gray-300 print:text-gray-700 mb-3">Certificados de Calibración</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 print:grid-cols-4 gap-4">
                                            {report.instrumentos.filter(i => i.certUrl).map((inst, idx) => (
                                                <div key={idx} className="border dark:border-gray-600 print:border-gray-200 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-750 print:bg-gray-50">
                                                    <a href={inst.certUrl} target="_blank" rel="noreferrer">
                                                        <img src={inst.certUrl} alt="Certificado" className="w-full h-32 object-cover hover:opacity-80 transition" />
                                                    </a>
                                                    <p className="p-2 text-xs text-center text-gray-600 dark:text-gray-400 print:text-gray-600 font-semibold truncate" title={`${inst.marca} ${inst.modelo}`}>
                                                        {inst.marca} {inst.modelo}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {report.circuitos?.some(c => c.fotoUrl) && (
                                    <div>
                                        <h3 className="font-bold text-gray-700 dark:text-gray-300 print:text-gray-700 mb-3">Registro Fotográfico de Circuitos</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 print:grid-cols-4 gap-4">
                                            {report.circuitos.filter(c => c.fotoUrl).map((circ, idx) => (
                                                <div key={idx} className="border dark:border-gray-600 print:border-gray-200 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-750 print:bg-gray-50">
                                                    <a href={circ.fotoUrl} target="_blank" rel="noreferrer">
                                                        <img src={circ.fotoUrl} alt="Medición Circuito" className="w-full h-32 object-cover hover:opacity-80 transition" />
                                                    </a>
                                                    <p className="p-2 text-xs text-center text-gray-600 dark:text-gray-400 print:text-gray-600 font-semibold truncate" title={circ.nombre}>
                                                        Medición {circ.nombre}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Fotos (Común) */}
                {report.fotos && report.fotos.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                        <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Registro Fotográfico</h2>
                        <div className={report.type === 'imagenes' ? 'space-y-6' : 'grid grid-cols-2 md:grid-cols-3 print:grid-cols-3 gap-4'}>
                            {report.fotos.map((foto, i) => (
                                report.type === 'imagenes' ? (
                                    <div key={i} className="border dark:border-gray-600 print:border-gray-200 rounded-xl p-4 bg-gray-50 dark:bg-gray-700/50 print:bg-gray-50 flex flex-col md:flex-row print:flex-row gap-4 print:break-inside-avoid">
                                        <img src={foto.url} alt={`Foto ${i + 1}`} className="w-full md:w-64 print:w-64 h-48 object-cover rounded-lg border dark:border-gray-600 print:border-gray-200 shadow" />
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-800 dark:text-gray-100 print:text-gray-800 mb-1">📷 {foto.caption || `Imagen ${i + 1}`}</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-600 whitespace-pre-wrap">{foto.description || 'Sin descripción.'}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <img key={i} src={foto.url} alt={`Foto ${i + 1}`} className="w-full h-40 object-cover rounded-lg border dark:border-gray-600 print:border-gray-200 shadow" />
                                )
                            ))}
                        </div>
                    </div>
                )}

                {/* Firma del Profesional */}
                {(report.profesionalNombre || report.profesionalRut || report.profesionalTitulo) && (
                    <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 print:border-gray-200 print:break-inside-avoid">
                        <h2 className="text-lg font-bold mb-4 text-blue-800 dark:text-blue-300 print:text-blue-800 border-b dark:border-gray-700 print:border-gray-200 pb-2">Profesional Responsable</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4">
                            <ReadOnlyField label="Nombre" value={report.profesionalNombre} />
                            <ReadOnlyField label="RUT" value={report.profesionalRut} />
                            <ReadOnlyField label="Título / Profesión" value={report.profesionalTitulo} />
                        </div>
                    </div>
                )}
            </div>

            {showEmailModal && (
                <EmailModal
                    report={report}
                    reportId={reportId}
                    onClose={() => setShowEmailModal(false)}
                    setNotification={setNotification}
                    companyName={companyName}
                />
            )}
        </div>
    );
};

export default ViewTechnicalReportPage;
